import { useState } from 'react';
import { Checkbox, Input } from '@/components/ui';

export interface CheckListItem {
  id: number;
  label: string;
  hint?: string;
}

/**
 * 勾选多项（CR-071）：工地设置里一次加几个人、用户页里指定这个人所在的工地。
 *
 * 用设计系统的 `Checkbox`（整行命中区，戴手套也点得中）；项目多了给一个搜索框，
 * 「全选」只作用于搜出来的那些、「清空」清全部。文案由调用方传进来 —— 这个控件不知道自己勾的是人还是工地。
 * 列表最高约 8 行，超了在自己里面滚，不把页面撑得很长。
 */
export function CheckList({
  items,
  selected,
  onChange,
  labels,
  searchable = items.length > 8,
}: {
  items: CheckListItem[];
  selected: number[];
  onChange: (next: number[]) => void;
  labels: { search: string; selectAll: string; clearAll: string; empty: string; picked: string };
  searchable?: boolean;
}) {
  const [query, setQuery] = useState('');
  const q = query.trim().toLowerCase();
  const shown = q
    ? items.filter((i) => i.label.toLowerCase().includes(q) || (i.hint ?? '').toLowerCase().includes(q))
    : items;
  const chosen = new Set(selected);

  const toggle = (id: number, on: boolean) => {
    const next = new Set(chosen);
    if (on) next.add(id);
    else next.delete(id);
    onChange([...next]);
  };

  return (
    <div className="flex flex-col gap-2">
      <div className="flex flex-wrap items-center gap-3">
        {searchable && (
          <div className="min-w-48 flex-1">
            <Input
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder={labels.search}
              aria-label={labels.search}
            />
          </div>
        )}
        <span className="text-body text-ink-3">{labels.picked}</span>
        <button
          type="button"
          className="min-h-touch px-2 text-body text-brand-600 hover:underline"
          onClick={() => onChange([...new Set([...selected, ...shown.map((i) => i.id)])])}
          disabled={shown.length === 0}
        >
          {labels.selectAll}
        </button>
        <button
          type="button"
          className="min-h-touch px-2 text-body text-brand-600 hover:underline"
          onClick={() => onChange([])}
          disabled={selected.length === 0}
        >
          {labels.clearAll}
        </button>
      </div>

      {shown.length === 0 ? (
        <p className="py-2 text-body text-ink-3">{labels.empty}</p>
      ) : (
        <div className="max-h-96 overflow-y-auto rounded-md border border-rule p-1">
          {shown.map((item) => (
            <Checkbox
              key={item.id}
              label={item.label}
              hint={item.hint}
              checked={chosen.has(item.id)}
              onChange={(e) => toggle(item.id, e.target.checked)}
            />
          ))}
        </div>
      )}
    </div>
  );
}
